import { CoveragePricingStrategy } from "@redotech/crdb-enums/coverage/coverage-pricing-strategy";
import { CoverageProductType } from "@redotech/crdb-enums/coverage/coverage-product-type";
import { coverageProductControlIdFormat } from "@redotech/redo-coverage-id";
import { PricingRuleSetSchema } from "@redotech/redo-return-settings-common/package-protection/pricing-rule-set";
import * as z from "zod";

/** Multiplies the price for the listed countries; an empty list is every other country. */
export const CoverageInternationalMultiplierSchema = z.object({
  countries: z.array(z.string()),
  multiplier: z.number().positive(),
});
export type CoverageInternationalMultiplier = z.infer<
  typeof CoverageInternationalMultiplierSchema
>;

const money = z.number().nonnegative();

/** pricing_strategy = fixed. */
export const FixedPricingSchema = z.object({ price: money });
export type FixedPricing = z.infer<typeof FixedPricingSchema>;

/** pricing_strategy = cart_total. */
export const CartTotalPricingSchema = z.object({
  pricingRuleSets: z.array(PricingRuleSetSchema),
  minPrice: money.nullable(),
  maxPrice: money.nullable(),
  /** Fraction of the cart total charged when no rule set applies. */
  fallbackPercentage: z.number().min(0).max(1).nullable(),
});
export type CartTotalPricing = z.infer<typeof CartTotalPricingSchema>;

/** pricing_strategy = item_count: base + additional × (items - 1), capped at max. */
export const ItemCountPricingSchema = z.object({
  basePrice: money,
  pricePerAdditionalItem: money,
  maxPrice: money,
});
export type ItemCountPricing = z.infer<typeof ItemCountPricingSchema>;

/** A coverage product's enablement and pricing (coverage_product_control), grouped by strategy. */
const CoverageProductControlFieldsSchema = z.object({
  type: z.enum(CoverageProductType),
  enabled: z.boolean(),
  pricingStrategy: z.enum(CoveragePricingStrategy),
  /** returns: amount billed to the merchant per order. */
  merchantPricePerOrder: money.nullable(),
  minEligibleCartTotal: money.nullable(),
  maxEligibleCartTotal: money.nullable(),
  /** package_protection: flat price when the cart has a subscription item. */
  fixedSubscriptionPrice: money.nullable(),
  /** final_sale: flat amount added on top of the item coverage price. */
  shippingLabelCoveragePrice: money.nullable(),
  /** returns: countries priced without a multiplier. */
  domesticCountries: z.array(z.string()).nullable(),
  internationalMultipliers: z.array(CoverageInternationalMultiplierSchema),
  fixed: FixedPricingSchema.nullable(),
  cartTotal: CartTotalPricingSchema.nullable(),
  itemCount: ItemCountPricingSchema.nullable(),
});

/** The chosen strategy's fields must be set; the others may be kept. */
function hasStrategyFields(
  control: z.infer<typeof CoverageProductControlFieldsSchema>,
): boolean {
  switch (control.pricingStrategy) {
    case CoveragePricingStrategy.Fixed:
      return control.fixed !== null;
    case CoveragePricingStrategy.CartTotal:
      return control.cartTotal !== null;
    case CoveragePricingStrategy.ItemCount:
      return control.itemCount !== null;
  }
}

const strategyFieldsMessage = {
  message: "The chosen pricing strategy's fields are missing",
};

export const CoverageProductControlWriteSchema =
  CoverageProductControlFieldsSchema.refine(
    hasStrategyFields,
    strategyFieldsMessage,
  );
export type CoverageProductControlWrite = z.infer<
  typeof CoverageProductControlWriteSchema
>;

export const CoverageProductControlSchema =
  CoverageProductControlFieldsSchema.extend({
    id: coverageProductControlIdFormat.schema,
    createdAt: z.date(),
  }).refine(hasStrategyFields, strategyFieldsMessage);
export type CoverageProductControl = z.infer<
  typeof CoverageProductControlSchema
>;
